import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { prefetchCriticalData, clearCache } from '../lib/dataCache';

const AuthContext = createContext(null);

// Auto-odjava nakon 30 min NEAKTIVNOSTI (bez korisničke interakcije).
// Bitno: broji se SAMO stvarna interakcija (miš/tipkovnica/scroll/touch), NE pozadinski
// pollovi (notes/pending, notifications svakih 7s) — inače se nikad ne bi okinula.
const IDLE_LIMIT_MS = 30 * 60 * 1000;
const IDLE_KEY = 'lastActivityAt';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      // Pre-fill IndexedDB cache in background after auth check
      prefetchCriticalData(api).catch(() => {});
    } catch (err) {
      // Wipe token SAMO ako je server eksplicitno rekao "token invalid" (401).
      // Mrežne greške, 500-ke (DB pool exhausted) i timeouts NE smiju izbaciti
      // korisnika — token je vjerojatno još uvijek validan. Retry će popraviti.
      if (err?.response?.status === 401) {
        localStorage.removeItem('token');
      } else {
        // Decode JWT lokalno — ako je validan i nije expired, držimo session živim.
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const stillValid = payload.exp && payload.exp * 1000 > Date.now();
          if (stillValid) {
            // Podržava i Lead token (sub/username) i CRM token (uid/email/name)
            setUser({
              id: payload.sub || payload.uid,
              username: payload.username || payload.email,
              display_name: payload.display_name || payload.name || payload.username || payload.email,
              role: payload.role,
              can_view_stats: payload.role === 'admin',
              can_view_finance: payload.role === 'admin',
            });
          } else {
            localStorage.removeItem('token');
          }
        } catch {
          // Token nije JWT → očisti
          localStorage.removeItem('token');
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    localStorage.setItem('token', data.token);
    setUser(data.user);
    // Start prefetching data immediately after login
    prefetchCriticalData(api).catch(() => {});
    return data;
  };

  // Prijava preko gotovog tokena (Leo CRM auth flow — 2FA/IP/Telegram odradi CRM).
  // CRM vraća { token, user:{id,email,name,role} }. Token se verificira na Lead backendu
  // (dual-verify CRM secret). Postavljamo user lokalno + /auth/me za potpune podatke.
  const loginWithToken = async (token, crmUser) => {
    localStorage.setItem('token', token);
    // Optimistično postavi iz CRM user objekta (ili decode-a)
    if (crmUser) {
      setUser({
        id: crmUser.id,
        username: crmUser.email,
        display_name: crmUser.name || crmUser.full_name || crmUser.email,
        role: crmUser.role,
        can_view_stats: crmUser.role === 'admin',
        can_view_finance: crmUser.role === 'admin',
      });
    }
    // Dohvati potpune podatke s Lead backenda (radi s CRM tokenom — dual-verify)
    try {
      const { data } = await api.get('/auth/me');
      if (data) setUser(data);
    } catch { /* optimistični user ostaje */ }
    prefetchCriticalData(api).catch(() => {});
  };

  const logout = useCallback((reason) => {
    // Lead-native: JWT je stateless, logout samo briše lokalni token (nema CRM sesije za
    // zatvoriti). Backend /api/auth/logout samo loga događaj — best-effort, ne blokira.
    try {
      const token = localStorage.getItem('token');
      if (token) {
        api.post('/auth/logout').catch(() => {});
      }
    } catch {}
    if (reason) { try { localStorage.setItem('logoutReason', reason); } catch {} }
    localStorage.removeItem('token');
    setUser(null);
    clearCache(); // Clear IndexedDB on logout
  }, []);

  // ── Sync odjave MEĐU TABOVIMA ──
  // Token je dijeljen (localStorage), ali React user-state NIJE. Bez ovoga: idle-odjava u
  // jednom (pozadinskom) tabu obriše token, a ostali tabovi ostanu "polumrtvi" — UI izgleda
  // prijavljen, ali SVI API pozivi vraćaju 403 → podaci/gumbi fantomski nestaju.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token' && !e.newValue) {
        setUser(null);            // → ProtectedRoute preusmjeri na /login
        try { clearCache(); } catch {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Auto-odjava nakon 30 min neaktivnosti ──
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    if (!user) return;
    const mark = () => { try { localStorage.setItem(IDLE_KEY, String(Date.now())); } catch {} };
    mark(); // reset na (re)login
    // throttle: zapiši aktivnost najviše ~svakih 5s (jeftino i na mousemove)
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 5000) { last = now; mark(); }
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click', 'focus'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    // provjera svakih 30s — ako je zadnja interakcija starija od 30 min → odjava
    const iv = setInterval(() => {
      const la = parseInt(localStorage.getItem(IDLE_KEY) || '0', 10);
      if (la && Date.now() - la > IDLE_LIMIT_MS) {
        clearInterval(iv);
        logoutRef.current('inactivity');
      }
    }, 30000);
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearInterval(iv);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
